import {useExport} from "@AppBuilderLib/entities/export/model/useExport";
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {useNotificationStore} from "@AppBuilderLib/features/notifications/model/useNotificationStore";
import {isPdfSrc} from "@AppBuilderLib/widgets/appbuilder/lib/isPdfSrc";
import {EXPORT_TYPE} from "@shapediver/viewer.session";
import {useCallback, useEffect, useRef, useState} from "react";
import {useShallow} from "zustand/react/shallow";
import AppBuilderImage from "./AppBuilderImage";
import AppBuilderPdfEmbed from "./AppBuilderPdfEmbed";

interface Props {
	/**
	 * Default session namespace to use for parameter and export references that do
	 * not specify a session namespace.
	 */
	namespace: string;
	/** Id or name or displayname of the export to get the image from. */
	exportId: string;
	anchor?: string;
	alt?: string;
	target?: string;
}

export default function AppBuilderImageExportWidgetComponent(props: Props) {
	const {namespace, exportId, anchor, target, alt} = props;
	const exportData = useExport({namespace, exportId});
	const definition = exportData?.definition;
	const actions = exportData?.actions;
	const notifications = useNotificationStore();

	const promiseChain = useRef(Promise.resolve());
	const objectUrl = useRef<string | undefined>(undefined);
	const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
	const [contentType, setContentType] = useState<string | undefined>(
		undefined,
	);

	/**
	 * Create an object URL for the given href and set it as the image source.
	 */
	const setImageSrcCb = useCallback(
		(href: string, responseContentType?: string) => {
			if (!actions) return;
			promiseChain.current = promiseChain.current.then(async () => {
				if (objectUrl.current) {
					URL.revokeObjectURL(objectUrl.current);
					objectUrl.current = undefined;
				}
				if (href) {
					const res = await actions.fetch(href);
					const blob = await res.blob();
					// blob: URLs have no .pdf suffix — need MIME for isPdfSrc
					setContentType(responseContentType || blob.type || undefined);
					objectUrl.current = URL.createObjectURL(blob);
				}
				setImageSrc(objectUrl.current);
			});
		},
		[actions],
	);

	// Register the export to be requested on every parameter change.
	const {registerDefaultExport, deregisterDefaultExport} =
		useShapeDiverStoreParameters(
			useShallow((state) => ({
				registerDefaultExport: state.registerDefaultExport,
				deregisterDefaultExport: state.deregisterDefaultExport,
			})),
		);

	useEffect(() => {
		if (!definition || !actions) {
			notifications.error({
				message: `Export ${exportId} not found`,
			});
			return;
		}
		registerDefaultExport(namespace, definition.id);

		return () => deregisterDefaultExport(namespace, definition.id);
	}, [namespace, definition, actions, exportId, notifications]);

	// Get responses to exports which were requested by default.
	const responses = useShapeDiverStoreParameters(
		(state) => state.defaultExportResponses[namespace],
	);

	useEffect(() => {
		if (!definition || !actions) return;

		if (responses && responses[definition.id]) {
			const response = responses[definition.id];
			if (
				response.content &&
				response.content[0] &&
				response.content[0].href
			) {
				const contentType = response.content[0].contentType;
				const href = response.content[0].href;
				setImageSrcCb(href, contentType);
			} else {
				setImageSrc(undefined);
				setContentType(undefined);
			}
		} else {
			promiseChain.current = promiseChain.current.then(async () => {
				if (definition.type !== EXPORT_TYPE.DOWNLOAD) return;

				// request the export
				const response = await actions.request();

				if (
					response.content &&
					response.content[0] &&
					response.content[0].href
				) {
					const href = response.content[0].href;
					const contentType = response.content[0].contentType;

					setImageSrcCb(href, contentType);
				} else {
					setImageSrc(undefined);
					setContentType(undefined);
				}
			});
		}
	}, [responses, definition, actions, setImageSrcCb]);

	if (!definition || !actions) {
		return <></>;
	}

	if (imageSrc) {
		if (isPdfSrc(imageSrc, contentType)) {
			return <AppBuilderPdfEmbed src={imageSrc} alt={alt} />;
		}
		return (
			<AppBuilderImage
				src={imageSrc}
				isSvg={contentType === "image/svg+xml"}
				anchor={anchor}
				target={target}
				alt={alt}
			/>
		);
	}
	return <></>;
}
